<template>
  <v-card class="heatmap-card" outlined>
    <v-card-title class="d-flex justify-space-between align-center flex-wrap">
      <div>
        <div class="text-h6">Outernet Billboard Intelligence Map</div>
        <div class="caption grey--text text--darken-1">
          Use filters and hotspot ranking to identify high-traffic billboard zones quickly.
        </div>
      </div>
      <div class="d-flex align-center flex-wrap">
        <v-chip color="primary" text-color="white" small class="mr-2 mb-1">
          {{ filteredEntries.length }} shown
        </v-chip>
        <v-chip color="grey lighten-3" small class="mb-1">
          {{ normalizedEntries.length }} total records
        </v-chip>
      </div>
    </v-card-title>

    <v-card-text>
      <v-row dense class="mb-2">
        <v-col cols="6" md="6">
          <v-sheet class="metric-card" outlined>
            <div class="caption grey--text text--darken-1">Submissions</div>
            <div class="text-h6 font-weight-bold">{{ filteredEntries.length }}</div>
          </v-sheet>
        </v-col>
        <v-col cols="6" md="6">
          <v-sheet class="metric-card" outlined>
            <div class="caption grey--text text--darken-1">Unique Brands</div>
            <div class="text-h6 font-weight-bold">
              <span v-if="brandTotalsLoadState === 'loading'" class="grey--text">…</span>
              <span v-else-if="brandTotalsLoadState === 'ok'">{{ kpiUniqueBrandsDisplay }}</span>
              <span v-else class="grey--text">—</span>
            </div>
            <div class="caption grey--text text--darken-1">
              Total brands:
              <template v-if="brandTotalsLoadState === 'loading'">…</template>
              <template v-else-if="brandTotalsLoadState === 'ok'">{{ kpiTotalBrandsDisplay }}</template>
              <template v-else>—</template>
            </div>
          </v-sheet>
        </v-col>
      </v-row>

      <v-row dense class="mb-3">
        <v-col cols="12" class="d-flex justify-end align-center flex-wrap">
          <v-btn
            small
            outlined
            color="deep-purple darken-1"
            class="mr-2 mb-1"
            :to="{ name: 'MobileV2OuternetCoverageMap' }"
          >
            <v-icon left small>mdi-map-marker-multiple</v-icon>
            Show comparison
          </v-btn>
          <v-btn small outlined class="mb-1" @click="fitToPoints(filteredEntries)">
            Fit Map
          </v-btn>
        </v-col>
      </v-row>

      <v-row dense class="mb-3">
        <v-col cols="12" md="8">
          <div class="d-flex align-center mb-2">
            <span class="caption font-weight-medium mr-2">Heat Radius</span>
            <span class="caption grey--text">{{ heatRadius }} px</span>
          </div>
          <v-slider
            v-model="heatRadius"
            :min="16"
            :max="90"
            :step="2"
            dense
            hide-details
          />
        </v-col>
        <v-col cols="12" md="4">
          <div class="d-flex align-center mb-2">
            <span class="caption font-weight-medium mr-2">Heat Opacity</span>
            <span class="caption grey--text">{{ heatOpacity.toFixed(2) }}</span>
          </div>
          <v-slider
            v-model="heatOpacity"
            :min="0.25"
            :max="1"
            :step="0.05"
            dense
            hide-details
          />
        </v-col>
      </v-row>

      <div class="d-flex align-center flex-wrap mb-3">
        <v-switch
          v-model="showHeatmap"
          label="Show heatmap"
          class="mt-0 pt-0 mr-4"
          hide-details
        />
        <v-switch
          v-model="showMarkers"
          label="Show pin markers"
          class="mt-0 pt-0 mr-4"
          hide-details
        />
        <v-switch
          v-model="isSatellite"
          label="Satellite map"
          class="mt-0 pt-0"
          hide-details
        />
      </div>
      <div v-if="showMarkers && isMarkerCapped" class="caption warning--text mb-3">
        Rendering {{ markerEntries.length }} of {{ filteredEntries.length }} markers for smooth
        performance.
      </div>
      <div v-else-if="!showMarkers && filteredEntries.length > autoDisableMarkerThreshold" class="caption warning--text mb-3">
        Markers auto-disabled for large dataset ({{ filteredEntries.length }} records). Turn on
        only when narrowed down.
      </div>

      <div ref="map" class="map-canvas" />

      <div class="legend mt-3">
        <span class="caption font-weight-medium mr-2">Density:</span>
        <div class="legend-gradient" />
        <div class="d-flex justify-space-between caption grey--text mt-1">
          <span>Low</span>
          <span>Medium</span>
          <span>High</span>
        </div>
      </div>

      <v-card outlined class="timing-insights mt-4">
        <v-card-title class="subtitle-1 font-weight-bold pb-1 d-flex align-center flex-wrap">
          <v-icon small color="indigo" class="mr-2">mdi-clock-time-four-outline</v-icon>
          Submission Time Insights (MYT, UTC+8)
        </v-card-title>
        <v-card-text class="pt-2">
          <div class="caption grey--text text--darken-1 mb-3">
            Built from submission `createdAt` timestamps converted from UTC into local Malaysia
            time.
          </div>

          <v-row dense class="mb-2">
            <v-col cols="12" sm="4">
              <div class="timing-kpi">
                <div class="timing-kpi__label">Peak Hour</div>
                <div class="timing-kpi__value">{{ peakHourLabel }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="timing-kpi">
                <div class="timing-kpi__label">Peak Share</div>
                <div class="timing-kpi__value">{{ formatPercent(peakHourSharePct) }}</div>
              </div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="timing-kpi">
                <div class="timing-kpi__label">Weekend Mix</div>
                <div class="timing-kpi__value">{{ formatPercent(weekendSharePct) }}</div>
              </div>
            </v-col>
          </v-row>

          <div class="d-flex flex-wrap mb-2">
            <v-chip
              v-for="item in timingInsightBadges"
              :key="item.label"
              small
              class="mr-2 mb-2"
              :color="item.color"
              text-color="white"
            >
              {{ item.label }}
            </v-chip>
          </div>

          <div v-if="hasTimingData" class="timing-bars">
            <div v-for="row in topTimingHours" :key="'hour-' + row.hour" class="timing-bar-row">
              <div class="timing-bar-row__label">{{ row.label }}</div>
              <div class="timing-bar-row__track">
                <div class="timing-bar-row__fill" :style="{ width: row.widthPct + '%' }" />
              </div>
              <div class="timing-bar-row__value">
                {{ row.count | hotspotNum }} ({{ formatPercent(row.sharePct) }})
              </div>
            </div>
          </div>
          <v-alert v-else dense outlined type="info" class="mb-0">
            No valid submission timestamps available yet.
          </v-alert>
        </v-card-text>
      </v-card>

      <v-card ref="inventoryInsightsCard" outlined class="inventory-insights mt-4">
        <v-card-title class="subtitle-1 font-weight-bold pb-1 d-flex align-center flex-wrap justify-space-between">
          <div class="d-flex align-center flex-wrap">
            <v-icon small color="teal darken-1" class="mr-2">mdi-warehouse</v-icon>
            Inventory Match Insights
          </div>
          <v-btn
            small
            color="teal darken-1"
            outlined
            class="mt-2 mt-sm-0"
            :to="{ name: 'MobileV2OuternetInventoryScanMap' }"
          >
            <v-icon left small>mdi-map-marker-radius</v-icon>
            Show on map
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-2">
          <v-progress-linear
            v-if="inventoryInsightsLoadState === 'loading'"
            indeterminate
            color="primary"
            height="3"
            class="mb-3 rounded-pill"
          />
          <v-alert
            v-else-if="inventoryInsightsLoadState === 'error'"
            dense
            outlined
            type="warning"
            class="mb-0"
          >
            Could not load inventory insights.
          </v-alert>
          <v-alert v-else-if="inventoryInsightsLoadState === 'idle'" dense outlined type="info" class="mb-0">
            Inventory insights load when this section is visible.
          </v-alert>
          <template v-else-if="hasInventoryInsights">
            <v-row dense class="mb-2">
              <v-col cols="6" md="4">
                <div class="inventory-kpi">
                  <div class="inventory-kpi__label">Total Submissions</div>
                  <div class="inventory-kpi__value">{{ inventoryKpis.totalSubmissions | hotspotNum }}</div>
                </div>
              </v-col>
              <v-col cols="6" md="4">
                <div class="inventory-kpi">
                  <div class="inventory-kpi__label">Matched</div>
                  <div class="inventory-kpi__value">{{ inventoryKpis.matchedSubmissions | hotspotNum }}</div>
                </div>
              </v-col>
              <v-col cols="6" md="4">
                <div class="inventory-kpi">
                  <div class="inventory-kpi__label">Unmatched</div>
                  <div class="inventory-kpi__value">{{ inventoryKpis.unmatchedSubmissions | hotspotNum }}</div>
                </div>
              </v-col>
              <v-col cols="6" md="4">
                <div class="inventory-kpi">
                  <div class="inventory-kpi__label">Match Rate</div>
                  <div class="inventory-kpi__value">{{ formatPercent(inventoryKpis.matchRatePct) }}</div>
                </div>
              </v-col>
              <v-col cols="6" md="4">
                <div class="inventory-kpi">
                  <div class="inventory-kpi__label">Inventory Gap Rate</div>
                  <div class="inventory-kpi__value">{{ formatPercent(inventoryKpis.inventoryGapRatePct) }}</div>
                </div>
              </v-col>
              <v-col v-if="!hideUniqueUserInsightMetrics" cols="6" md="4">
                <div class="inventory-kpi">
                  <div class="inventory-kpi__label">Total Unique Users</div>
                  <div class="inventory-kpi__value">{{ inventoryKpis.totalUniqueUsers | hotspotNum }}</div>
                </div>
              </v-col>
            </v-row>

            <div class="d-flex flex-wrap mb-2">
              <v-chip
                v-for="badge in inventoryInsightBadges"
                :key="badge.label"
                x-small
                class="mr-1 mb-1"
                :color="badge.color"
                text-color="white"
              >
                {{ badge.label }}
              </v-chip>
            </div>

            <v-row dense>
              <v-col cols="12" md="6">
                <div class="inventory-block mb-2">
                  <div class="subtitle-2 font-weight-bold mb-2">Coverage by Scan Status</div>
                  <div class="inventory-list">
                    <div
                      v-for="row in inventoryCoverageRows"
                      :key="'coverage-' + row.label"
                      class="inventory-row"
                    >
                      <div class="inventory-row__title">{{ row.label }}</div>
                      <div class="inventory-row__bar-bg">
                        <div class="inventory-row__bar-fill" :style="{ width: row.barWidth + '%' }" />
                      </div>
                      <div class="inventory-row__meta">
                        {{ row.count | hotspotNum }} ({{ formatPercent(row.sharePct) }})
                      </div>
                    </div>
                  </div>
                </div>

                <div class="inventory-block mb-2">
                  <div class="subtitle-2 font-weight-bold mb-2">Top States</div>
                  <div class="inventory-list">
                    <div v-for="row in inventoryTopStatesRows" :key="'state-' + row.name" class="inventory-row">
                      <div class="inventory-row__title">{{ row.name }}</div>
                      <div class="inventory-row__bar-bg">
                        <div class="inventory-row__bar-fill" :style="{ width: row.barWidth + '%' }" />
                      </div>
                      <div class="inventory-row__meta">
                        {{ row.count | hotspotNum }} | {{ formatPercent(row.matchRatePct) }} match
                      </div>
                    </div>
                  </div>
                </div>

                <div class="inventory-block">
                  <div class="subtitle-2 font-weight-bold mb-2">Top Cities</div>
                  <div class="inventory-list">
                    <div v-for="row in inventoryTopCitiesRows" :key="'city-' + row.name" class="inventory-row">
                      <div class="inventory-row__title">{{ row.name }}</div>
                      <div class="inventory-row__bar-bg">
                        <div class="inventory-row__bar-fill" :style="{ width: row.barWidth + '%' }" />
                      </div>
                      <div class="inventory-row__meta">
                        {{ row.count | hotspotNum }} | {{ row.unmatched | hotspotNum }} unmatched
                      </div>
                    </div>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="inventory-block mb-2">
                  <div class="subtitle-2 font-weight-bold mb-2">Top Brands (Gap Risk)</div>
                  <div class="inventory-list">
                    <div v-for="row in inventoryTopBrandsRows" :key="'brand-' + row.name" class="inventory-row">
                      <div class="inventory-row__title">{{ row.name }}</div>
                      <div class="inventory-row__bar-bg">
                        <div class="inventory-row__bar-fill" :style="{ width: row.barWidth + '%' }" />
                      </div>
                      <div class="inventory-row__meta">
                        {{ row.count | hotspotNum }} | {{ formatPercent(row.unmatchedRatePct) }} unmatched
                      </div>
                    </div>
                  </div>
                </div>

                <div class="inventory-block mb-2">
                  <div class="subtitle-2 font-weight-bold mb-2">Top Media Owners</div>
                  <div class="inventory-list">
                    <div v-for="row in inventoryTopMediaOwnerRows" :key="'owner-' + row.name" class="inventory-row">
                      <div class="inventory-row__title">{{ row.name }}</div>
                      <div class="inventory-row__bar-bg">
                        <div class="inventory-row__bar-fill" :style="{ width: row.barWidth + '%' }" />
                      </div>
                      <div class="inventory-row__meta">{{ row.count | hotspotNum }}</div>
                    </div>
                  </div>
                </div>

                <div class="inventory-block mb-2">
                  <div class="subtitle-2 font-weight-bold mb-2">Top Unmatched Areas</div>
                  <div class="inventory-list">
                    <div v-for="row in inventoryTopUnmatchedAreaRows" :key="'unmatched-area-' + row.name" class="inventory-row">
                      <div class="inventory-row__title">{{ row.name }}</div>
                      <div class="inventory-row__bar-bg">
                        <div class="inventory-row__bar-fill" :style="{ width: row.barWidth + '%' }" />
                      </div>
                      <div class="inventory-row__meta">{{ row.count | hotspotNum }}</div>
                    </div>
                  </div>
                </div>

                <div class="inventory-block">
                  <div class="subtitle-2 font-weight-bold mb-2">Matched Environment Breakdown</div>
                  <div class="inventory-list">
                    <div v-for="row in inventoryEnvironmentRows" :key="'env-' + row.name" class="inventory-row">
                      <div class="inventory-row__title text-capitalize">{{ row.name }}</div>
                      <div class="inventory-row__bar-bg">
                        <div class="inventory-row__bar-fill" :style="{ width: row.barWidth + '%' }" />
                      </div>
                      <div class="inventory-row__meta">
                        {{ row.count | hotspotNum }} ({{ formatPercent(row.sharePct) }})
                      </div>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </template>
          <v-alert v-else dense outlined type="info" class="mb-0">
            No inventory insights available yet.
          </v-alert>
        </v-card-text>
      </v-card>

      <!-- Server snapshot -->
      <v-card outlined class="hotspots-snapshot mt-4">
        <div class="hotspots-snapshot__head pa-4 pb-2">
          <div class="d-flex flex-wrap justify-space-between align-start">
            <div class="pr-4 mb-2 mb-md-0">
              <div class="text-h6 font-weight-bold d-flex align-center flex-wrap">
                <span class="hotspots-snapshot__title-icon mr-2">
                  <v-icon color="white" size="20">mdi-chart-timeline-variant</v-icon>
                </span>
                Top locations
              </div>
              <div class="caption grey--text text--darken-1 mt-2 hotspots-snapshot__sub">
                <template v-if="isRankedHotspotSnapshot">
                  Administrative top {{ rankedHotspotLimit }} hotspots by submission volume. Map
                  filters below only affect pins and heat, not this list.
                </template>
                <template v-else>
                  Fast read on where outernet submissions are concentrated. Map filters below only
                  affect pins and heat - not this snapshot.
                </template>
              </div>
            </div>
            <v-btn
              small
              depressed
              color="primary"
              class="white--text"
              :loading="hotspotsLoadState === 'loading'"
              :disabled="hotspotsLoadState === 'loading'"
              @click="fetchServerHotspots"
            >
              <v-icon left small>mdi-refresh</v-icon>
              Refresh
            </v-btn>
          </div>

          <v-progress-linear
            v-if="hotspotsLoadState === 'loading'"
            indeterminate
            color="primary"
            height="3"
            class="mt-4 mb-0 rounded-pill"
          />

          <div v-else-if="hotspotsLoadState === 'error'" class="mt-4">
            <v-alert dense outlined type="warning" class="mb-0 body-2">
              Could not load top locations. Please try refresh.
            </v-alert>
          </div>

          <div v-else-if="hasHotspotSnapshot" class="mt-4">
            <v-row v-if="isRankedHotspotSnapshot" dense class="hotspot-kpi-row mb-1">
              <v-col cols="6" sm="4">
                <div class="hotspot-kpi">
                  <div class="hotspot-kpi__label">Ranked zones</div>
                  <div class="hotspot-kpi__value">{{ hotspotClusterCount | hotspotNum }}</div>
                </div>
              </v-col>
              <v-col cols="6" sm="4">
                <div class="hotspot-kpi">
                  <div class="hotspot-kpi__label">Submissions (this list)</div>
                  <div class="hotspot-kpi__value">{{ clusteredSubmissionCount | hotspotNum }}</div>
                </div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="hotspot-kpi">
                  <div class="hotspot-kpi__label">#1 zone submissions</div>
                  <div class="hotspot-kpi__value">{{ topRankedHotspotSubmissionCount | hotspotNum }}</div>
                </div>
              </v-col>
              <v-col v-if="!hideUniqueUserInsightMetrics" cols="12" sm="4">
                <div class="hotspot-kpi">
                  <div class="hotspot-kpi__label">Total unique users (page)</div>
                  <div class="hotspot-kpi__value">{{ totalUniqueUsersKpi | hotspotNum }}</div>
                </div>
              </v-col>
            </v-row>

            <v-row v-else dense class="hotspot-kpi-row mb-1">
              <v-col cols="6" md="4">
                <div class="hotspot-kpi">
                  <div class="hotspot-kpi__label">Total Hotspots</div>
                  <div class="hotspot-kpi__value">{{ hotspotClusterCount | hotspotNum }}</div>
                </div>
              </v-col>
              <v-col cols="6" md="4">
                <div class="hotspot-kpi">
                  <div class="hotspot-kpi__label">Clustered Submissions</div>
                  <div class="hotspot-kpi__value">{{ clusteredSubmissionCount | hotspotNum }}</div>
                </div>
              </v-col>
              <v-col v-if="!hideUniqueUserInsightMetrics" cols="6" md="4">
                <div class="hotspot-kpi">
                  <div class="hotspot-kpi__label">Total Unique Users</div>
                  <div class="hotspot-kpi__value">{{ totalUniqueUsersKpi | hotspotNum }}</div>
                </div>
              </v-col>
              <v-col cols="6" md="4">
                <div class="hotspot-kpi">
                  <div class="hotspot-kpi__label">Cluster Radius</div>
                  <div class="hotspot-kpi__value">
                    {{ formatKm(serverRadiusMeters) }}<span class="hotspot-kpi__unit">km</span>
                  </div>
                </div>
              </v-col>
              <v-col cols="6" md="4">
                <div class="hotspot-kpi">
                  <div class="hotspot-kpi__label">Contributors (Approx)</div>
                  <div class="hotspot-kpi__value">{{ contributorCountApprox | hotspotNum }}</div>
                </div>
              </v-col>
            </v-row>

            <div v-if="isRankedHotspotSnapshot" class="mt-2">
              <div class="caption grey--text text--darken-1 mb-2">
                Bar length is relative to the #1 zone. "Top brands" counts are submissions in that
                zone (sample), not global brand totals.
              </div>
              <div class="hotspot-ranked-list">
                <v-sheet
                  v-for="row in visibleServerHotspotRows"
                  :key="row.hotspotKey || 'hs-' + row.rank + '-' + row.name"
                  outlined
                  class="hotspot-ranked-row pa-3 mb-2"
                >
                  <div class="d-flex flex-wrap align-start justify-space-between mb-2">
                    <div class="d-flex align-start flex-grow-1" style="min-width: 0">
                      <div
                        class="hotspot-ranked-row__rank mr-3 flex-shrink-0"
                        :class="{
                          'hotspot-ranked-row__rank--gold': row.rank === 1,
                          'hotspot-ranked-row__rank--silver': row.rank === 2,
                          'hotspot-ranked-row__rank--bronze': row.rank === 3,
                        }"
                      >
                        <span class="hotspot-ranked-row__rank-num">{{ row.rank }}</span>
                      </div>
                      <div class="flex-grow-1" style="min-width: 0">
                        <div class="text-body-1 font-weight-bold hotspot-ranked-row__title">
                          {{ row.shortTitle }}
                        </div>
                        <div
                          class="caption grey--text text--darken-1 mt-1 hotspot-ranked-row__label"
                          :title="row.name"
                        >
                          {{ row.name }}
                        </div>
                      </div>
                    </div>
                    <v-btn
                      icon
                      small
                      color="primary"
                      class="hotspot-map-btn flex-shrink-0"
                      :title="hasApproxCoords(row) ? 'Center map on approximate location' : 'Search map for this address'"
                      :disabled="!map || (!hasApproxCoords(row) && !canGeocodeFocus)"
                      @click="focusServerHotspot(row)"
                    >
                      <v-icon small>mdi-map-search-outline</v-icon>
                    </v-btn>
                  </div>

                  <div class="hotspot-row__bar-wrap mb-2">
                    <div class="hotspot-row__bar-bg">
                      <div
                        class="hotspot-row__bar-fill"
                        :class="{
                          'hotspot-row__bar-fill--gold': row.rank === 1,
                          'hotspot-row__bar-fill--silver': row.rank === 2,
                          'hotspot-row__bar-fill--bronze': row.rank === 3,
                        }"
                        :style="{ width: Math.max(2, row.submissionSharePct) + '%' }"
                      />
                    </div>
                    <span class="hotspot-row__pct caption grey--text text--darken-1">
                      vs #1 {{ formatPercent(row.submissionSharePct) }}
                    </span>
                  </div>

                  <div class="d-flex flex-wrap align-center mb-2">
                    <v-chip x-small class="mr-2 mb-1" color="primary" text-color="white">
                      {{ row.submissionCount | hotspotNum }} submissions
                    </v-chip>
                    <v-chip v-if="row.shareOfTop10Pct > 0" x-small outlined class="mr-2 mb-1">
                      {{ formatPercent(row.shareOfTop10Pct) }} of this list
                    </v-chip>
                    <v-chip
                      v-if="row.groupingLabel"
                      x-small
                      outlined
                      class="mr-2 mb-1"
                    >
                      {{ row.groupingLabel }}
                    </v-chip>
                  </div>

                  <div v-if="row.topBrands.length" class="d-flex flex-wrap align-center">
                    <span class="caption grey--text text--darken-1 mr-2 mb-1">Top brands:</span>
                    <v-chip
                      v-for="brand in row.topBrands"
                      :key="(row.hotspotKey || row.rank) + '-' + brand.brand"
                      x-small
                      outlined
                      class="mr-1 mb-1"
                    >
                      {{ brand.brand }} · {{ brand.count | hotspotNum }}
                    </v-chip>
                  </div>
                </v-sheet>

                <div v-if="!visibleServerHotspotRows.length" class="pa-6 text-center caption grey--text">
                  No ranked hotspots returned for this view.
                </div>
              </div>
            </div>

            <div v-else class="hotspot-layout mt-3">
              <div class="hotspot-layout__left">
                <div class="subtitle-2 font-weight-bold mb-2">Top Areas</div>
                <div class="hotspot-list-wrap">
                  <div
                    v-for="area in topAreaRows"
                    :key="'top-area-' + area.rank"
                    class="hotspot-row d-flex align-stretch"
                  >
                    <div class="hotspot-row__rank">
                      <span class="hotspot-row__rank-num">{{ area.rank }}</span>
                    </div>
                    <div class="hotspot-row__body flex-grow-1 py-3 pl-3 pr-2">
                      <div class="hotspot-row__title text-body-2 font-weight-medium">
                        {{ area.name }}
                      </div>
                      <div class="hotspot-row__bar-wrap mt-2">
                        <div class="hotspot-row__bar-bg">
                          <div
                            class="hotspot-row__bar-fill"
                            :style="{ width: Math.max(2, area.submissionSharePct) + '%' }"
                          />
                        </div>
                        <span class="hotspot-row__pct caption grey--text text--darken-1">
                          {{ formatPercent(area.submissionSharePct) }}
                        </span>
                      </div>
                    </div>
                    <div
                      class="hotspot-row__actions d-flex flex-column align-center justify-center px-2 py-2"
                    >
                      <div class="hotspot-row__count-num primary--text">
                        {{ area.submissionCount | hotspotNum }}
                      </div>
                      <div class="caption grey--text text--darken-1 text-center">
                        Submissions
                      </div>
                    </div>
                  </div>
                  <div v-if="!topAreaRows.length" class="pa-6 text-center caption grey--text">
                    No top areas available.
                  </div>
                </div>
              </div>

              <div class="hotspot-layout__right">
                <div class="subtitle-2 font-weight-bold mb-2">Hotspot Details</div>
                <div class="hotspot-cards">
                  <v-sheet
                    v-for="row in visibleServerHotspotRows"
                    :key="'hs-' + row.rank + '-' + row.name"
                    outlined
                    class="hotspot-detail-card pa-3 mb-2"
                  >
                    <div class="d-flex flex-wrap justify-space-between align-start mb-1">
                      <div class="mr-2">
                        <div class="text-body-1 font-weight-bold">
                          #{{ row.rank }} {{ row.name }}
                        </div>
                        <div class="caption grey--text text--darken-1">
                          {{ row.cityStateLabel }}
                        </div>
                      </div>
                      <v-btn
                        icon
                        x-small
                        color="primary"
                        class="hotspot-map-btn"
                        :disabled="!canGeocodeFocus"
                        @click="focusServerHotspot(row)"
                      >
                        <v-icon small>mdi-map-search-outline</v-icon>
                      </v-btn>
                    </div>

                    <div class="d-flex flex-wrap align-center mb-2">
                      <v-chip x-small class="mr-2 mb-1" color="primary" text-color="white">
                        {{ row.submissionCount | hotspotNum }} submissions
                      </v-chip>
                      <v-chip x-small outlined class="mr-2 mb-1">
                        {{ formatPercent(row.submissionSharePct) }} share
                      </v-chip>
                      <v-chip x-small outlined class="mr-2 mb-1">
                        {{ row.uniqueContributorCount | hotspotNum }} contributors
                      </v-chip>
                    </div>

                    <div class="d-flex flex-wrap mb-2">
                      <v-chip
                        v-for="insight in row.insights"
                        :key="row.name + insight.label"
                        x-small
                        class="mr-1 mb-1"
                        :color="insight.color"
                        text-color="white"
                      >
                        {{ insight.label }}
                      </v-chip>
                    </div>

                    <div class="d-flex flex-wrap mb-1">
                      <v-chip
                        v-for="brand in row.topBrands"
                        :key="row.name + brand.brand"
                        x-small
                        outlined
                        class="mr-1 mb-1"
                      >
                        {{ brand.brand }} ({{ brand.count }})
                      </v-chip>
                    </div>

                    <div class="caption grey--text text--darken-1">
                      First: {{ formatDate(row.firstSubmissionAt) }} | Last:
                      {{ formatDate(row.lastSubmissionAt) }}
                    </div>
                  </v-sheet>

                  <div v-if="!visibleServerHotspotRows.length" class="pa-6 text-center caption grey--text">
                    No hotspots match your filters.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-else-if="hotspotsLoadState === 'ok' && !hasHotspotSnapshot"
            class="mt-4 caption grey--text"
          >
            No hotspot snapshot data in the response.
          </div>

          <div
            v-if="hotspotsLoadState === 'ok' && hotspotClusterCount === 0"
            class="mt-3"
          >
            <v-alert dense outlined type="info" class="mb-0">
              {{ hotspotHintMessage }}
            </v-alert>
          </div>
        </div>
      </v-card>

      <v-card ref="brandInsightsCard" outlined class="brand-insights mt-4">
        <v-card-title class="subtitle-1 font-weight-bold pb-1 d-flex align-center flex-wrap">
          <v-icon small color="deep-purple darken-1" class="mr-2">mdi-tag-multiple</v-icon>
          Brand Insights
        </v-card-title>
        <v-card-text class="pt-2">
          <v-progress-linear
            v-if="brandInsightsLoadState === 'loading'"
            indeterminate
            color="primary"
            height="3"
            class="mb-3 rounded-pill"
          />
          <v-alert v-else-if="brandInsightsLoadState === 'error'" dense outlined type="warning" class="mb-0">
            Could not load brand insights.
          </v-alert>
          <v-alert v-else-if="brandInsightsLoadState === 'idle'" dense outlined type="info" class="mb-0">
            Brand insights load when this section is visible.
          </v-alert>
          <template v-else-if="hasBrandInsights">
            <v-row dense class="mb-2">
              <v-col cols="6" md="4">
                <div class="brand-kpi">
                  <div class="brand-kpi__label">Leading Brand</div>
                  <div class="brand-kpi__value brand-kpi__value--text">{{ topBrandName }}</div>
                </div>
              </v-col>
              <v-col cols="6" md="4">
                <div class="brand-kpi">
                  <div class="brand-kpi__label">Total Submissions (Top Brands)</div>
                  <div class="brand-kpi__value">{{ totalBrandSubmissions | hotspotNum }}</div>
                </div>
              </v-col>
              <v-col cols="6" md="4">
                <div class="brand-kpi">
                  <div class="brand-kpi__label">Avg Inventory Match</div>
                  <div class="brand-kpi__value">{{ formatPercent(avgBrandInventoryMatchRatePct) }}</div>
                </div>
              </v-col>
            </v-row>

            <div class="d-flex flex-wrap mb-2">
              <v-chip
                v-for="badge in brandInsightBadges"
                :key="badge.label"
                x-small
                class="mr-1 mb-1"
                :color="badge.color"
                text-color="white"
              >
                {{ badge.label }}
              </v-chip>
            </div>

            <div class="brand-table-wrap">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th class="text-left">Brand</th>
                    <th class="text-left">Submissions</th>
                    <th class="text-left">Users</th>
                    <th class="text-left">Inventory Match</th>
                    <th class="text-left">Top City</th>
                    <th class="text-left">Latest</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in brandRows" :key="'brand-row-' + row.brand">
                    <td class="font-weight-medium">{{ row.brand }}</td>
                    <td>{{ row.submissions | hotspotNum }}</td>
                    <td>{{ row.uniqueUsers | hotspotNum }}</td>
                    <td>
                      {{ row.inventoryMatched | hotspotNum }} /
                      {{ row.submissions | hotspotNum }}
                      ({{ formatPercent(row.inventoryMatchRatePct) }})
                    </td>
                    <td>{{ row.topCity || "UNKNOWN" }}</td>
                    <td>{{ formatDate(row.latestSubmissionAt) }}</td>
                  </tr>
                  <tr v-if="!brandRows.length">
                    <td colspan="6" class="grey--text text--darken-1">No brand insights found.</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </template>
          <v-alert v-else dense outlined type="info" class="mb-0">
            No brand insights available yet.
          </v-alert>
        </v-card-text>
      </v-card>

      <v-card
        v-if="!hideUserSubmissionInsights"
        ref="userSubmissionsCard"
        outlined
        class="user-submissions-insights mt-4"
      >
        <v-card-title class="subtitle-1 font-weight-bold pb-1 d-flex align-center flex-wrap">
          <v-icon small color="blue darken-2" class="mr-2">mdi-account-multiple</v-icon>
          User Submission Insights
        </v-card-title>
        <v-card-text class="pt-2">
          <v-progress-linear
            v-if="userSubmissionsLoadState === 'loading'"
            indeterminate
            color="primary"
            height="3"
            class="mb-3 rounded-pill"
          />
          <v-alert v-else-if="userSubmissionsLoadState === 'error'" dense outlined type="warning" class="mb-0">
            Could not load user submission insights.
          </v-alert>
          <v-alert v-else-if="userSubmissionsLoadState === 'idle'" dense outlined type="info" class="mb-0">
            User submission insights load when this section is visible.
          </v-alert>
          <template v-else-if="hasUserSubmissionsInsights">
            <v-row dense class="mb-2">
              <v-col cols="6" md="4">
                <div class="user-kpi">
                  <div class="user-kpi__label">Contributors Listed</div>
                  <div class="user-kpi__value">{{ userSubmissionsUniqueUsersKpi | hotspotNum }}</div>
                </div>
              </v-col>
              <v-col cols="6" md="4">
                <div class="user-kpi">
                  <div class="user-kpi__label">Top User Submissions</div>
                  <div class="user-kpi__value">{{ topUserSubmissionCount | hotspotNum }}</div>
                </div>
              </v-col>
              <v-col cols="6" md="4">
                <div class="user-kpi">
                  <div class="user-kpi__label">Avg Submissions / User</div>
                  <div class="user-kpi__value">{{ avgUserSubmissionCount | hotspotNum }}</div>
                </div>
              </v-col>
            </v-row>

            <div class="d-flex flex-wrap mb-2">
              <v-chip
                v-for="badge in userSubmissionBadges"
                :key="badge.label"
                x-small
                class="mr-1 mb-1"
                :color="badge.color"
                text-color="white"
              >
                {{ badge.label }}
              </v-chip>
            </div>

            <div class="user-table-wrap">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th class="text-left">User</th>
                    <th class="text-left">Email</th>
                    <th class="text-left">User ID</th>
                    <th class="text-left">Submissions</th>
                    <th class="text-left">First Submission</th>
                    <th class="text-left">Last Submission</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in userSubmissionRows" :key="'user-row-' + row.userId">
                    <td class="font-weight-medium">{{ row.firstName || "Unknown" }}</td>
                    <td>{{ row.email || "N/A" }}</td>
                    <td class="text-truncate user-id-cell">{{ row.userId }}</td>
                    <td>{{ row.totalSubmissions | hotspotNum }}</td>
                    <td>{{ formatDate(row.firstSubmissionAt) }}</td>
                    <td>{{ formatDate(row.lastSubmissionAt) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </template>
          <v-alert v-else dense outlined type="info" class="mb-0">
            No user submission insights available yet.
          </v-alert>
        </v-card-text>
      </v-card>
    </v-card-text>
  </v-card>
</template>

<script>
import RestResource from "../services/dataServiceMobileV2.js";

const hotspotApi = new RestResource();
const INSIGHTS_CACHE_TTL_MS = 3 * 60 * 1000;
const insightsCache = {
  inventory: { data: null, fetchedAt: 0 },
  brand: { data: null, fetchedAt: 0 },
  users: { data: null, fetchedAt: 0 },
};

export default {
  name: "HeatmapView",

  props: {
    data: {
      type: Array,
      required: true,
    },
    totalUniqueUsers: {
      type: Number,
      default: 0,
    },
    hideUserSubmissionInsights: {
      type: Boolean,
      default: false,
    },
    hideUniqueUserInsightMetrics: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      hotspotsPayload: null,
      hotspotsLoadState: "loading",
      inventoryInsightsPayload: null,
      inventoryInsightsLoadState: "idle",
      brandInsightsPayload: null,
      brandInsightsLoadState: "idle",
      userSubmissionsPayload: null,
      userSubmissionsLoadState: "idle",
      userSubmissionsTotalUniqueUsers: 0,
      hotspotListExpanded: true,
      hotspotPreviewLimit: 20,
      map: null,
      heatmap: null,
      markers: [],
      infoWindow: null,
      showHeatmap: true,
      showMarkers: true,
      isSatellite: false,
      heatRadius: 46,
      heatOpacity: 0.74,
      redrawDebounceMs: 250,
      redrawTimer: null,
      markerRenderLimit: 500,
      autoDisableMarkerThreshold: 1200,
      sectionObserver: null,
      brandTotalsPayload: null,
      brandTotalsLoadState: "idle",
    };
  },

  computed: {
    hasHotspotSnapshot() {
      return (
        this.hotspotsLoadState === "ok" &&
        this.hotspotsPayload &&
        typeof this.hotspotsPayload === "object"
      );
    },

    isRankedHotspotSnapshot() {
      return Boolean(
        this.hotspotsPayload && this.hotspotsPayload.hotspotSnapshotKind === "rankedList"
      );
    },

    rankedHotspotLimit() {
      const m = this.hotspotsPayload && this.hotspotsPayload.meta;
      const lim = m && Number(m.limit);
      if (Number.isFinite(lim) && lim > 0) {
        return lim;
      }
      const n = (this.hotspotsPayload && this.hotspotsPayload.hotspots) || [];
      return Array.isArray(n) ? n.length : 10;
    },

    topRankedHotspotSubmissionCount() {
      const rows = this.filteredServerHotspotRows;
      if (!rows.length) {
        return 0;
      }
      return Number(rows[0].submissionCount) || 0;
    },

    hasInventoryInsights() {
      return (
        this.inventoryInsightsLoadState === "ok" &&
        this.inventoryInsightsPayload &&
        typeof this.inventoryInsightsPayload === "object"
      );
    },

    hasBrandInsights() {
      return (
        this.brandInsightsLoadState === "ok" &&
        Array.isArray(this.brandInsightsPayload) &&
        this.brandInsightsPayload.length > 0
      );
    },

    hasUserSubmissionsInsights() {
      return (
        this.userSubmissionsLoadState === "ok" &&
        Array.isArray(this.userSubmissionsPayload) &&
        this.userSubmissionsPayload.length > 0
      );
    },

    userSubmissionRows() {
      if (!Array.isArray(this.userSubmissionsPayload)) {
        return [];
      }
      return this.userSubmissionsPayload
        .map(item => ({
          userId: item.userId || "N/A",
          firstName: item.firstName || "",
          email: item.email || "",
          totalSubmissions: Number(item.totalSubmissions) || 0,
          firstSubmissionAt:
            this.toDateValue(item.firstSubmissionAt) || item.firstSubmissionAt || null,
          lastSubmissionAt:
            this.toDateValue(item.lastSubmissionAt) || item.lastSubmissionAt || null,
        }))
        .sort((a, b) => b.totalSubmissions - a.totalSubmissions)
        .slice(0, 25);
    },

    topUserSubmissionCount() {
      return this.userSubmissionRows.length ? this.userSubmissionRows[0].totalSubmissions : 0;
    },

    userSubmissionsUniqueUsersKpi() {
      if (this.userSubmissionsTotalUniqueUsers > 0) {
        return this.userSubmissionsTotalUniqueUsers;
      }
      return this.userSubmissionRows.length;
    },

    avgUserSubmissionCount() {
      if (!this.userSubmissionRows.length) {
        return 0;
      }
      const sum = this.userSubmissionRows.reduce((acc, row) => acc + row.totalSubmissions, 0);
      return Math.round(sum / this.userSubmissionRows.length);
    },

    userSubmissionBadges() {
      if (!this.userSubmissionRows.length) {
        return [];
      }
      const badges = [];
      const topUser = this.userSubmissionRows[0];
      const topUserLabel = topUser.firstName || topUser.userId;
      const recentlyActive = this.userSubmissionRows.filter(row =>
        this.isWithinLastDays(row.lastSubmissionAt, 7)
      ).length;

      badges.push({
        label: `Top contributor: ${topUserLabel} (${topUser.totalSubmissions})`,
        color: "blue darken-2",
      });
      badges.push({
        label: `${recentlyActive} users active in last 7 days`,
        color: recentlyActive >= 5 ? "green darken-1" : "orange darken-2",
      });
      return badges;
    },

    brandRows() {
      if (!Array.isArray(this.brandInsightsPayload)) {
        return [];
      }
      return this.brandInsightsPayload
        .map(item => ({
          brand: item.brand || "Unknown",
          submissions: Number(item.submissions) || 0,
          approved: Number(item.approved) || 0,
          rejected: Number(item.rejected) || 0,
          inReview: Number(item.inReview) || 0,
          inventoryMatched: Number(item.inventoryMatched) || 0,
          uniqueUsers: Number(item.uniqueUsers) || 0,
          inventoryMatchRatePct: Number(item.inventoryMatchRatePct) || 0,
          latestSubmissionAt: this.toDateValue(item.latestSubmissionAt) || item.latestSubmissionAt,
          topCity: item.topCity || "UNKNOWN",
        }))
        .sort((a, b) => b.submissions - a.submissions);
    },

    topBrandName() {
      return this.brandRows.length ? this.brandRows[0].brand : "N/A";
    },

    totalBrandSubmissions() {
      return this.brandRows.reduce((acc, row) => acc + row.submissions, 0);
    },

    avgBrandInventoryMatchRatePct() {
      if (!this.brandRows.length) {
        return 0;
      }
      const sum = this.brandRows.reduce((acc, row) => acc + row.inventoryMatchRatePct, 0);
      return sum / this.brandRows.length;
    },

    brandInsightBadges() {
      if (!this.brandRows.length) {
        return [];
      }
      const badges = [];
      const highestGapBrand = this.brandRows
        .slice()
        .sort((a, b) => b.inventoryMatchRatePct - a.inventoryMatchRatePct)[0];
      const lowestGapBrand = this.brandRows
        .slice()
        .sort((a, b) => a.inventoryMatchRatePct - b.inventoryMatchRatePct)[0];

      if (this.avgBrandInventoryMatchRatePct >= 50) {
        badges.push({ label: "Healthy brand-to-inventory alignment", color: "green darken-1" });
      } else {
        badges.push({ label: "Brand inventory coverage needs improvement", color: "orange darken-2" });
      }

      if (highestGapBrand) {
        badges.push({
          label: `Best inventory match: ${highestGapBrand.brand} (${this.formatPercent(highestGapBrand.inventoryMatchRatePct)})`,
          color: "blue darken-2",
        });
      }
      if (lowestGapBrand) {
        badges.push({
          label: `Largest gap risk: ${lowestGapBrand.brand} (${this.formatPercent(lowestGapBrand.inventoryMatchRatePct)})`,
          color: "red darken-1",
        });
      }
      return badges;
    },

    inventoryKpis() {
      const k = (this.inventoryInsightsPayload && this.inventoryInsightsPayload.kpis) || {};
      return {
        totalSubmissions: Number(k.totalSubmissions) || 0,
        matchedSubmissions: Number(k.matchedSubmissions) || 0,
        unmatchedSubmissions: Number(k.unmatchedSubmissions) || 0,
        totalUniqueUsers: Number(k.totalUniqueUsers) || 0,
        matchRatePct: Number(k.matchRatePct) || 0,
        inventoryGapRatePct: Number(k.inventoryGapRatePct) || 0,
      };
    },

    inventoryCoverageRows() {
      const list =
        (this.inventoryInsightsPayload &&
          this.inventoryInsightsPayload.inventoryCoverageByScanStatus) ||
        [];
      const total = Math.max(1, this.inventoryKpis.totalSubmissions);
      const maxCount = Math.max(1, ...list.map(item => Number(item.count) || 0));
      return list.map(item => ({
        label: item.inventoryScanStatus === "matched" ? "Matched" : "No Match",
        count: Number(item.count) || 0,
        sharePct: ((Number(item.count) || 0) / total) * 100,
        barWidth: Math.max(4, ((Number(item.count) || 0) / maxCount) * 100),
      }));
    },

    inventoryTopStatesRows() {
      const list = (this.inventoryInsightsPayload && this.inventoryInsightsPayload.topStates) || [];
      const maxCount = Math.max(1, ...list.map(item => Number(item.count) || 0));
      return list.slice(0, 6).map(item => {
        const count = Number(item.count) || 0;
        const matched = Number(item.matched) || 0;
        return {
          name: item.state || "UNKNOWN",
          count,
          matchRatePct: count > 0 ? (matched / count) * 100 : 0,
          barWidth: Math.max(4, (count / maxCount) * 100),
        };
      });
    },

    inventoryTopCitiesRows() {
      const list = (this.inventoryInsightsPayload && this.inventoryInsightsPayload.topCities) || [];
      const maxCount = Math.max(1, ...list.map(item => Number(item.count) || 0));
      return list.slice(0, 6).map(item => {
        const city = item.city || "UNKNOWN";
        const state = item.state || "UNKNOWN";
        const count = Number(item.count) || 0;
        return {
          name: `${city}, ${state}`,
          count,
          unmatched: Number(item.unmatched) || 0,
          barWidth: Math.max(4, (count / maxCount) * 100),
        };
      });
    },

    inventoryTopBrandsRows() {
      const list = (this.inventoryInsightsPayload && this.inventoryInsightsPayload.topBrands) || [];
      const maxCount = Math.max(1, ...list.map(item => Number(item.count) || 0));
      return list.slice(0, 8).map(item => {
        const count = Number(item.count) || 0;
        const unmatched = Number(item.unmatched) || 0;
        return {
          name: item.brand || "Unknown",
          count,
          unmatchedRatePct: count > 0 ? (unmatched / count) * 100 : 0,
          barWidth: Math.max(4, (count / maxCount) * 100),
        };
      });
    },

    inventoryTopMediaOwnerRows() {
      const list =
        (this.inventoryInsightsPayload && this.inventoryInsightsPayload.topMediaOwners) || [];
      const maxCount = Math.max(1, ...list.map(item => Number(item.count) || 0));
      return list.slice(0, 8).map(item => {
        const count = Number(item.count) || 0;
        return {
          name: item.mediaOwnerName || "Unknown",
          count,
          barWidth: Math.max(4, (count / maxCount) * 100),
        };
      });
    },

    inventoryTopUnmatchedAreaRows() {
      const list =
        (this.inventoryInsightsPayload && this.inventoryInsightsPayload.topUnmatchedAreas) || [];
      const maxCount = Math.max(1, ...list.map(item => Number(item.count) || 0));
      return list.slice(0, 6).map(item => {
        const city = item.city || "UNKNOWN";
        const state = item.state || "UNKNOWN";
        const count = Number(item.count) || 0;
        return {
          name: `${city}, ${state}`,
          count,
          barWidth: Math.max(4, (count / maxCount) * 100),
        };
      });
    },

    inventoryEnvironmentRows() {
      const list =
        (this.inventoryInsightsPayload &&
          this.inventoryInsightsPayload.matchedEnvironmentBreakdown) ||
        [];
      const total = Math.max(
        1,
        list.reduce((acc, item) => acc + (Number(item.count) || 0), 0)
      );
      const maxCount = Math.max(1, ...list.map(item => Number(item.count) || 0));
      return list.map(item => {
        const count = Number(item.count) || 0;
        return {
          name: item.environment || "unknown",
          count,
          sharePct: (count / total) * 100,
          barWidth: Math.max(4, (count / maxCount) * 100),
        };
      });
    },

    inventoryInsightBadges() {
      if (!this.hasInventoryInsights) {
        return [];
      }
      const badges = [];
      if (this.inventoryKpis.matchRatePct >= 60) {
        badges.push({ label: "Strong inventory coverage", color: "green darken-1" });
      } else if (this.inventoryKpis.matchRatePct >= 40) {
        badges.push({ label: "Moderate match performance", color: "orange darken-2" });
      } else {
        badges.push({ label: "High inventory gap risk", color: "red darken-1" });
      }

      const unknownState = this.inventoryTopStatesRows.find(r => r.name === "UNKNOWN");
      if (unknownState && unknownState.count >= this.inventoryKpis.totalSubmissions * 0.4) {
        badges.push({ label: "Location quality issue (UNKNOWN heavy)", color: "deep-purple darken-1" });
      }

      const topBrandRisk = this.inventoryTopBrandsRows[0];
      if (topBrandRisk && topBrandRisk.unmatchedRatePct >= 70) {
        badges.push({ label: `Brand gap hotspot: ${topBrandRisk.name}`, color: "blue darken-2" });
      }
      return badges;
    },

    serverRadiusMeters() {
      return Number(this.hotspotsPayload && this.hotspotsPayload.radiusMeters) || 0;
    },

    clusteredSubmissionCount() {
      if (!this.hotspotsPayload) {
        return 0;
      }
      const direct = Number(
        this.hotspotsPayload.totals && this.hotspotsPayload.totals.clusteredSubmissions
      );
      if (Number.isFinite(direct)) {
        return direct;
      }
      const sum = (this.hotspotsPayload.hotspots || []).reduce(
        (acc, h) => acc + (Number(h.submissionCount) || 0),
        0
      );
      return Math.max(0, sum);
    },

    hotspotClusterCount() {
      if (!this.hotspotsPayload) {
        return 0;
      }
      const c = Number(this.hotspotsPayload.clusters);
      if (Number.isFinite(c) && c >= 0) {
        return c;
      }
      return (this.hotspotsPayload.hotspots || this.hotspotsPayload.topAreas || []).length;
    },

    contributorCountApprox() {
      if (!this.hotspotsPayload) {
        return 0;
      }
      return (
        Number(
          this.hotspotsPayload.totals &&
            this.hotspotsPayload.totals.contributorCountApprox
        ) || 0
      );
    },

    totalUniqueUsersKpi() {
      return Number(this.totalUniqueUsers) || 0;
    },

    topAreaRows() {
      if (!this.hotspotsPayload || !Array.isArray(this.hotspotsPayload.topAreas)) {
        return [];
      }
      return this.hotspotsPayload.topAreas.map((area, idx) => ({
        rank: Number(area.rank) || idx + 1,
        name: area.name || "Unnamed Area",
        submissionCount: Number(area.submissionCount) || 0,
        submissionSharePct: Number(area.submissionSharePct) || 0,
      }));
    },

    serverHotspotRows() {
      if (!this.hotspotsPayload || !Array.isArray(this.hotspotsPayload.hotspots)) {
        return [];
      }
      return this.hotspotsPayload.hotspots.map((h, idx) => {
        const address = h.address || {};
        const city = address.city || "";
        const state = address.state || "";
        const cityState = [city, state].filter(Boolean).join(", ");
        const firstSubmissionAt = this.toDateValue(h.firstSubmissionAt);
        const lastSubmissionAt = this.toDateValue(h.lastSubmissionAt);
        const brandCount = b =>
          Number(b.submissionCount) || Number(b.count) || Number(b.submissions) || 0;
        const insightsPreset = Array.isArray(h.insights)
          ? h.insights
          : this.getHotspotInsights({
              submissionSharePct: Number(h.submissionSharePct) || 0,
              uniqueContributorCount: Number(h.uniqueContributorCount) || 0,
              lastSubmissionAt,
            });
        const groupingRaw = h.grouping != null ? String(h.grouping).trim() : "";
        const groupingLabel =
          groupingRaw && groupingRaw.toLowerCase() === "administrative"
            ? "Administrative zone"
            : groupingRaw || "";
        return {
          rank: Number(h.rank) || idx + 1,
          name: h.name || "Unnamed Area",
          shortTitle: h.shortTitle || cityState || (h.name || "Unnamed Area"),
          submissionCount: Number(h.submissionCount) || 0,
          submissionSharePct: Number(h.submissionSharePct) || 0,
          shareOfTop10Pct: Number(h.shareOfTop10Pct) || 0,
          uniqueContributorCount: Number(h.uniqueContributorCount) || 0,
          topBrands: Array.isArray(h.topBrands)
            ? h.topBrands
                .map(b => ({
                  brand: b.brand || "Unknown",
                  count: brandCount(b),
                }))
                .slice(0, 3)
            : [],
          locality: address.locality || "",
          city,
          state,
          country: address.country || "",
          postcode: address.postcode || "",
          cityStateKey: cityState || "Unknown",
          cityStateLabel: cityState || "Unknown location",
          firstSubmissionAt,
          lastSubmissionAt,
          insights: insightsPreset,
          lat: h.lat != null ? Number(h.lat) : null,
          lng: h.lng != null ? Number(h.lng) : h.lon != null ? Number(h.lon) : null,
          hotspotKey: h.hotspotKey || null,
          groupingLabel,
        };
      });
    },

    filteredServerHotspotRows() {
      return this.serverHotspotRows.slice().sort((a, b) => {
        if (a.rank && b.rank) {
          return a.rank - b.rank;
        }
        return b.submissionSharePct - a.submissionSharePct;
      });
    },

    visibleServerHotspotRows() {
      const rows = this.filteredServerHotspotRows;
      if (this.hotspotListExpanded || rows.length <= this.hotspotPreviewLimit) {
        return rows;
      }
      return rows.slice(0, this.hotspotPreviewLimit);
    },

    hotspotHintMessage() {
      if (!this.hotspotsPayload) {
        return "No hotspot clusters found yet.";
      }
      if (this.isRankedHotspotSnapshot && !(this.hotspotsPayload.hotspots || []).length) {
        return "No ranked hotspot rows were returned. Try again later or adjust reporting filters upstream.";
      }
      return (
        this.hotspotsPayload.hint ||
        this.hotspotsPayload.emptyHint ||
        this.hotspotsPayload.message ||
        "No hotspot clusters found yet. Try widening date range or location coverage."
      );
    },

    canGeocodeFocus() {
      return Boolean(
        this.map && window.google && window.google.maps && window.google.maps.Geocoder
      );
    },

    normalizedEntries() {
      return this.data
        .map((item, index) => {
          const location = item && item.location;
          const coords = location && location.coordinates;
          if (!Array.isArray(coords) || coords.length < 2) {
            return null;
          }

          const lng = Number(coords[0]);
          const lat = Number(coords[1]);
          if (Number.isNaN(lat) || Number.isNaN(lng)) {
            return null;
          }

          return {
            id: item._id || `entry-${index}`,
            lat,
            lng,
            brand: item.brand || "Unknown",
            rawBrand: item.brand,
            hasBrandField: Object.prototype.hasOwnProperty.call(item || {}, "brand"),
            status: item.status || "Unknown",
            type: item.type || null,
            isDuplicate: Boolean(item.isDuplicate),
            createdAt: item.createdAt || null,
          };
        })
        .filter(Boolean);
    },

    filteredEntries() {
      return this.normalizedEntries;
    },

    kpiUniqueBrandsDisplay() {
      const p = this.brandTotalsPayload;
      if (!p) {
        return "—";
      }
      const n = Number(p.totalUniqueBrandsAcrossApprovedSubmissions);
      return Number.isFinite(n) ? n : "—";
    },

    kpiTotalBrandsDisplay() {
      const p = this.brandTotalsPayload;
      if (!p) {
        return "—";
      }
      const n = Number(p.totalBrandsAcrossApprovedSubmissions);
      return Number.isFinite(n) ? n : "—";
    },

    hourlyTimingBuckets() {
      const buckets = Array.from({ length: 24 }, (_, hour) => ({
        hour,
        count: 0,
      }));
      this.filteredEntries.forEach(entry => {
        const hour = this.extractMalaysiaHour(entry.createdAt);
        if (hour != null) {
          buckets[hour].count += 1;
        }
      });
      return buckets;
    },

    totalTimedSubmissions() {
      return this.hourlyTimingBuckets.reduce((acc, bucket) => acc + bucket.count, 0);
    },

    hasTimingData() {
      return this.totalTimedSubmissions > 0;
    },

    peakHourBucket() {
      if (!this.hasTimingData) {
        return null;
      }
      return this.hourlyTimingBuckets.reduce((best, current) => {
        if (!best || current.count > best.count) {
          return current;
        }
        return best;
      }, null);
    },

    peakHourLabel() {
      if (!this.peakHourBucket) {
        return "N/A";
      }
      return this.formatHourRange(this.peakHourBucket.hour);
    },

    peakHourSharePct() {
      if (!this.peakHourBucket || !this.totalTimedSubmissions) {
        return 0;
      }
      return (this.peakHourBucket.count / this.totalTimedSubmissions) * 100;
    },

    weekendSharePct() {
      if (!this.totalTimedSubmissions) {
        return 0;
      }
      const weekend = this.filteredEntries.reduce((acc, entry) => {
        const weekday = this.extractMalaysiaWeekday(entry.createdAt);
        if (weekday === "Sat" || weekday === "Sun") {
          return acc + 1;
        }
        return acc;
      }, 0);
      return (weekend / this.totalTimedSubmissions) * 100;
    },

    topTimingHours() {
      if (!this.hasTimingData) {
        return [];
      }
      const maxCount = Math.max(...this.hourlyTimingBuckets.map(b => b.count), 1);
      return this.hourlyTimingBuckets
        .filter(b => b.count > 0)
        .map(b => ({
          hour: b.hour,
          label: this.formatHourRange(b.hour),
          count: b.count,
          sharePct: (b.count / this.totalTimedSubmissions) * 100,
          widthPct: Math.max(3, (b.count / maxCount) * 100),
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6);
    },

    timingInsightBadges() {
      if (!this.hasTimingData) {
        return [];
      }
      const badges = [];
      const peakHour = this.peakHourBucket ? this.peakHourBucket.hour : null;
      if (peakHour != null) {
        if (peakHour >= 7 && peakHour <= 10) {
          badges.push({ label: "Morning commute activity", color: "indigo darken-1" });
        } else if (peakHour >= 11 && peakHour <= 15) {
          badges.push({ label: "Midday concentration", color: "teal darken-1" });
        } else if (peakHour >= 16 && peakHour <= 21) {
          badges.push({ label: "Evening prime window", color: "deep-purple darken-1" });
        } else {
          badges.push({ label: "Late-hour skew", color: "orange darken-2" });
        }
      }
      if (this.weekendSharePct >= 35) {
        badges.push({ label: "Strong weekend demand", color: "green darken-1" });
      } else {
        badges.push({ label: "Weekday-led demand", color: "blue darken-1" });
      }
      if (this.peakHourSharePct >= 12) {
        badges.push({ label: "Concentrated submission window", color: "red darken-1" });
      } else {
        badges.push({ label: "Balanced timing spread", color: "cyan darken-2" });
      }
      return badges;
    },

    clusteredHotspots() {
      const clusters = {};
      this.filteredEntries.forEach(item => {
        const key = `${item.lat.toFixed(4)},${item.lng.toFixed(4)}`;
        if (!clusters[key]) {
          clusters[key] = {
            key,
            lat: item.lat,
            lng: item.lng,
            count: 0,
            brands: {},
          };
        }

        clusters[key].count += 1;
        clusters[key].brands[item.brand] = (clusters[key].brands[item.brand] || 0) + 1;
      });

      return Object.values(clusters)
        .map(cluster => {
          const sortedBrands = Object.keys(cluster.brands).sort(
            (a, b) => cluster.brands[b] - cluster.brands[a]
          );
          const topBrand = sortedBrands[0];
          return {
            key: cluster.key,
            lat: cluster.lat,
            lng: cluster.lng,
            count: cluster.count,
            topBrand: topBrand || "Unknown",
            topBrandCount: topBrand ? cluster.brands[topBrand] : 0,
          };
        })
        .sort((a, b) => b.count - a.count);
    },

    topHotspots() {
      return this.clusteredHotspots.slice(0, 5);
    },

    weightedPoints() {
      return this.clusteredHotspots;
    },

    markerEntries() {
      return this.filteredEntries.slice(0, this.markerRenderLimit);
    },

    isMarkerCapped() {
      return this.filteredEntries.length > this.markerRenderLimit;
    },
  },

  filters: {
    hotspotNum(value) {
      if (value == null || Number.isNaN(Number(value))) {
        return "—";
      }
      return Number(value).toLocaleString();
    },
  },

  watch: {
    filteredEntries() {
      if (
        this.filteredEntries.length > this.autoDisableMarkerThreshold &&
        this.showMarkers
      ) {
        this.showMarkers = false;
      }
      this.logBrandCounts();
      this.scheduleRefreshMapData();
    },
    showMarkers() {
      this.renderMarkers();
    },
    showHeatmap() {
      this.toggleHeatmapVisibility();
    },
    isSatellite() {
      if (this.map) {
        this.map.setMapTypeId(this.isSatellite ? "hybrid" : "roadmap");
      }
    },
    heatRadius() {
      this.applyHeatmapStyle();
    },
    heatOpacity() {
      this.applyHeatmapStyle();
    },
    data: {
      handler() {
        this.fetchBrandTotals();
      },
    },
  },

  methods: {
    logBrandCounts() {
      console.log("[HeatMap] Map rows / brand totals (API)", {
        mapRowCount: this.filteredEntries.length,
        brandTotalsLoadState: this.brandTotalsLoadState,
        brandTotalsPayload: this.brandTotalsPayload,
      });
    },

    getCachedInsight(key) {
      const cacheEntry = insightsCache[key];
      if (!cacheEntry || !cacheEntry.data) {
        return null;
      }
      if (Date.now() - cacheEntry.fetchedAt > INSIGHTS_CACHE_TTL_MS) {
        return null;
      }
      return cacheEntry.data;
    },

    setCachedInsight(key, data) {
      insightsCache[key] = {
        data,
        fetchedAt: Date.now(),
      };
    },

    setupSectionLazyLoading() {
      if (typeof window === "undefined" || !window.IntersectionObserver) {
        this.loadVisibleInsights(true);
        return;
      }

      this.sectionObserver = new window.IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            if (this._lazyLoadFallbackTimer) {
              clearTimeout(this._lazyLoadFallbackTimer);
              this._lazyLoadFallbackTimer = null;
            }
            const el = entry.target;
            const inventoryEl = this.resolveRefElement(this.$refs.inventoryInsightsCard);
            const brandEl = this.resolveRefElement(this.$refs.brandInsightsCard);
            const usersEl = this.hideUserSubmissionInsights
              ? null
              : this.resolveRefElement(this.$refs.userSubmissionsCard);

            if (inventoryEl && el === inventoryEl) {
              this.fetchInventoryInsights();
              this.sectionObserver.unobserve(el);
            } else if (brandEl && el === brandEl) {
              this.fetchBrandInsights();
              this.sectionObserver.unobserve(el);
            } else if (usersEl && el === usersEl) {
              this.fetchTotalUserSubmissions();
              this.sectionObserver.unobserve(el);
            }
          });
        },
        { root: null, rootMargin: "120px 0px", threshold: 0 }
      );

      const observeRefs = [this.$refs.inventoryInsightsCard, this.$refs.brandInsightsCard];
      if (!this.hideUserSubmissionInsights) {
        observeRefs.push(this.$refs.userSubmissionsCard);
      }
      const elements = observeRefs
        .map(ref => this.resolveRefElement(ref))
        .filter(Boolean);

      if (elements.length === 0) {
        this.loadVisibleInsights(true);
        return;
      }

      elements.forEach(el => this.sectionObserver.observe(el));

      this._lazyLoadFallbackTimer = setTimeout(() => {
        this.loadVisibleInsights(false);
      }, 3000);
    },

    resolveRefElement(ref) {
      if (!ref) {
        return null;
      }
      if (ref instanceof Element) {
        return ref;
      }
      if (ref.$el && ref.$el instanceof Element) {
        return ref.$el;
      }
      return null;
    },

    loadVisibleInsights(force = false) {
      const tasks = [];
      if (force || this.inventoryInsightsLoadState === "idle") {
        tasks.push(this.fetchInventoryInsights(force));
      }
      if (force || this.brandInsightsLoadState === "idle") {
        tasks.push(this.fetchBrandInsights(force));
      }
      if (
        !this.hideUserSubmissionInsights &&
        (force || this.userSubmissionsLoadState === "idle")
      ) {
        tasks.push(this.fetchTotalUserSubmissions(force));
      }
      return Promise.allSettled(tasks);
    },

    scheduleRefreshMapData() {
      if (this.redrawTimer) {
        clearTimeout(this.redrawTimer);
      }

      this.redrawTimer = setTimeout(() => {
        this.refreshMapData();
      }, this.redrawDebounceMs);
    },

    loadGoogleMapsScript() {
      if (window.google && window.google.maps) {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        const existingScript = document.getElementById("google-maps-script");
        if (existingScript) {
          existingScript.addEventListener("load", resolve, { once: true });
          existingScript.addEventListener(
            "error",
            () => reject(new Error("Failed to load Google Maps script")),
            { once: true }
          );
          return;
        }

        const script = document.createElement("script");
        script.id = "google-maps-script";
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_GOOGLE_MAP}&v=weekly&libraries=visualization`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = () => reject(new Error("Failed to load Google Maps script"));
        document.head.appendChild(script);
      });
    },

    initMap() {
      const { google } = window;
      this.map = new google.maps.Map(this.$refs.map, {
        zoom: 13,
        center: { lat: 5.9608, lng: 116.0865 },
        mapTypeId: "roadmap",
        streetViewControl: false,
        fullscreenControl: true,
        mapTypeControl: false,
      });
      this.infoWindow = new google.maps.InfoWindow();
    },

    parseHotspotsPayload(res) {
      const body = res && res.data;
      if (!body || typeof body !== "object") {
        return null;
      }

      if (
        body &&
        (Array.isArray(body.hotspots) ||
          Array.isArray(body.topAreas) ||
          body.clusters != null ||
          body.totals)
      ) {
        return body;
      }

      if (
        body.data &&
        typeof body.data === "object" &&
        !Array.isArray(body.data) &&
        (Array.isArray(body.data.hotspots) ||
          Array.isArray(body.data.topAreas) ||
          body.data.clusters != null ||
          body.data.totals)
      ) {
        return body.data;
      }

      const rankedRows = Array.isArray(body.data) ? body.data : null;
      const firstRanked = rankedRows && rankedRows.length ? rankedRows[0] : null;
      const rowLooksRanked =
        firstRanked &&
        typeof firstRanked === "object" &&
        (typeof firstRanked.hotspotKey === "string" ||
          (firstRanked.label != null &&
            Number(firstRanked.rank) >= 1 &&
            (firstRanked.approximateLatitude != null ||
              firstRanked.approximateLongitude != null ||
              firstRanked.grouping === "administrative")));
      const envelopeLooksRanked =
        rankedRows != null &&
        (body.meta != null ||
          body.isSuccess === true ||
          rowLooksRanked ||
          rankedRows.length === 0);

      if (envelopeLooksRanked && rankedRows != null && (rankedRows.length === 0 || rowLooksRanked)) {
        const shortLocationTitle = h => {
          const city = String(h.city || "").trim();
          const state = String(h.state || "").trim();
          const pc = String(h.postcode || "").trim();
          const line = [city, state].filter(Boolean).join(", ");
          if (line && pc) {
            return `${line} · ${pc}`;
          }
          if (line) {
            return line;
          }
          if (pc) {
            return `Postcode ${pc}`;
          }
          const label = String(h.label || "").trim();
          if (!label) {
            return "Location";
          }
          return label.length > 56 ? `${label.slice(0, 53)}…` : label;
        };

        const counts = rankedRows.map(r => Number(r.submissionCount) || 0);
        const maxCount = counts.length ? Math.max(...counts) : 0;
        const totalSubmissions = counts.reduce((s, n) => s + n, 0);
        const shareVsLeader = sub => (maxCount > 0 ? (sub / maxCount) * 100 : 0);

        const hotspots = rankedRows.map((h, idx) => {
          const sub = Number(h.submissionCount) || 0;
          const shareOfTop10Pct = totalSubmissions > 0 ? (sub / totalSubmissions) * 100 : 0;
          const label = String(h.label || "Unnamed location").trim() || "Unnamed location";
          return {
            rank: Number(h.rank) || idx + 1,
            name: label,
            shortTitle: shortLocationTitle(h),
            submissionCount: sub,
            submissionSharePct: shareVsLeader(sub),
            shareOfTop10Pct,
            uniqueContributorCount: 0,
            firstSubmissionAt: null,
            lastSubmissionAt: null,
            insights: [],
            topBrands: Array.isArray(h.topBrands)
              ? h.topBrands.map(b => ({
                  brand: b.brand || "Unknown",
                  count: Number(b.submissionCount) || Number(b.count) || 0,
                }))
              : [],
            address: {
              city: h.city || "",
              state: h.state || "",
              country: h.country || "",
              postcode: h.postcode || "",
              locality: "",
            },
            lat:
              h.approximateLatitude != null
                ? Number(h.approximateLatitude)
                : h.lat != null
                  ? Number(h.lat)
                  : null,
            lng:
              h.approximateLongitude != null
                ? Number(h.approximateLongitude)
                : h.lng != null
                  ? Number(h.lng)
                  : h.lon != null
                    ? Number(h.lon)
                    : null,
            hotspotKey: h.hotspotKey || null,
            grouping: h.grouping || null,
          };
        });

        const topAreas = hotspots.map(h => ({
          rank: h.rank,
          name: h.shortTitle,
          submissionCount: h.submissionCount,
          submissionSharePct: h.submissionSharePct,
        }));

        return {
          hotspotSnapshotKind: "rankedList",
          meta: body.meta || null,
          hotspots,
          topAreas,
          clusters: hotspots.length,
          totals: {
            clusteredSubmissions: totalSubmissions,
            contributorCountApprox: 0,
          },
          radiusMeters: 0,
          contributorCountApprox: 0,
          message: body.message,
        };
      }

      return null;
    },

    parseInventoryInsightsPayload(res) {
      const body = res && res.data;
      if (!body || typeof body !== "object") {
        return null;
      }
      if (body.kpis || body.topStates || body.topCities || body.topBrands) {
        return body;
      }
      if (body.data && typeof body.data === "object") {
        return body.data;
      }
      return null;
    },

    parseBrandInsightsPayload(res) {
      const body = res && res.data;
      if (!body || typeof body !== "object") {
        return [];
      }

      const looksLikeBrandRow = row =>
        row && typeof row === "object" && ("brand" in row || "submissions" in row);

      const extractArray = value => {
        if (Array.isArray(value)) {
          return value.length && looksLikeBrandRow(value[0]) ? value : [];
        }
        return [];
      };

      // common response shapes
      const direct = extractArray(body);
      if (direct.length) return direct;

      const level1 = extractArray(body.data);
      if (level1.length) return level1;

      const level2 = body.data && extractArray(body.data.data);
      if (level2 && level2.length) return level2;

      const keyed =
        extractArray(body.topBrands) ||
        extractArray(body.brandInsights) ||
        extractArray(body.results) ||
        (body.data &&
          (extractArray(body.data.topBrands) ||
            extractArray(body.data.brandInsights) ||
            extractArray(body.data.results)));

      if (keyed && keyed.length) return keyed;

      return [];
    },

    parseUserSubmissionsPayload(res) {
      const body = res && res.data;
      if (!body || typeof body !== "object") {
        return [];
      }

      const looksLikeUserRow = row =>
        row && typeof row === "object" && ("userId" in row || "totalSubmissions" in row);
      const validArray = value =>
        Array.isArray(value) && value.length && looksLikeUserRow(value[0]) ? value : [];

      const candidates = [
        body.users,
        body.topUsers,
        body.userSubmissions,
        body.data,
        body.data && body.data.users,
        body.data && body.data.topUsers,
        body.data && body.data.userSubmissions,
        body.data && body.data.data,
      ];

      for (let i = 0; i < candidates.length; i += 1) {
        const arr = validArray(candidates[i]);
        if (arr.length) {
          return arr;
        }
      }
      return [];
    },

    parseBrandTotalsBody(res) {
      const body = res && res.data;
      if (!body || typeof body !== "object") {
        return null;
      }
      const nested = body.data && typeof body.data === "object" && !Array.isArray(body.data);
      const src = nested ? body.data : body;
      const total = Number(src.totalBrandsAcrossApprovedSubmissions);
      const unique = Number(src.totalUniqueBrandsAcrossApprovedSubmissions);
      if (!Number.isFinite(total) && !Number.isFinite(unique)) {
        return null;
      }
      return {
        totalBrandsAcrossApprovedSubmissions: Number.isFinite(total) ? total : null,
        totalUniqueBrandsAcrossApprovedSubmissions: Number.isFinite(unique) ? unique : null,
      };
    },

    async fetchBrandTotals() {
      this.brandTotalsLoadState = "loading";
      try {
        const res = await hotspotApi.getBrandTotals({});
        const parsed = this.parseBrandTotalsBody(res);
        if (parsed) {
          this.brandTotalsPayload = parsed;
          this.brandTotalsLoadState = "ok";
        } else {
          this.brandTotalsPayload = null;
          this.brandTotalsLoadState = "error";
        }
      } catch (e) {
        this.brandTotalsPayload = null;
        this.brandTotalsLoadState = "error";
      }
    },

    async fetchServerHotspots() {
      this.hotspotsLoadState = "loading";
      try {
        const res = await hotspotApi.getHotSpots();
        const payload = this.parseHotspotsPayload(res);
        if (payload) {
          this.hotspotsPayload = payload;
          this.hotspotListExpanded = true;
          this.hotspotsLoadState = "ok";
        } else {
          this.hotspotsPayload = null;
          this.hotspotsLoadState = "error";
        }
      } catch (e) {
        this.hotspotsPayload = null;
        this.hotspotsLoadState = "error";
      }
    },

    focusServerHotspot(row) {
      if (
        row.lat != null &&
        row.lng != null &&
        !Number.isNaN(row.lat) &&
        !Number.isNaN(row.lng)
      ) {
        this.focusHotspot({ lat: row.lat, lng: row.lng });
        return;
      }
      if (!this.map || !this.canGeocodeFocus || !row.name) {
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      const query = [row.name, row.locality, row.city, row.state, row.postcode, row.country]
        .filter(Boolean)
        .join(", ");
      geocoder.geocode({ address: query || `${row.name}, Malaysia` }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          const loc = results[0].geometry.location;
          this.map.panTo(loc);
          this.map.setZoom(14);
        }
      });
    },

    hasApproxCoords(row) {
      const lat = Number(row && row.lat);
      const lng = Number(row && row.lng);
      return Number.isFinite(lat) && Number.isFinite(lng);
    },

    toDateValue(value) {
      if (!value) {
        return null;
      }
      if (typeof value === "string") {
        return value;
      }
      if (typeof value === "object" && value.$date) {
        return value.$date;
      }
      return null;
    },

    formatDate(value) {
      if (!value) {
        return "N/A";
      }
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) {
        return "N/A";
      }
      return d.toLocaleDateString();
    },

    formatPercent(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) {
        return "0.0%";
      }
      return `${n.toFixed(1)}%`;
    },

    formatKm(meters) {
      const n = Number(meters);
      if (!Number.isFinite(n) || n <= 0) {
        return "0.0";
      }
      return (n / 1000).toFixed(1);
    },

    isWithinLastDays(value, days) {
      if (!value) {
        return false;
      }
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) {
        return false;
      }
      const diff = Date.now() - d.getTime();
      return diff >= 0 && diff <= days * 24 * 60 * 60 * 1000;
    },

    getHotspotInsights(hotspot) {
      const tags = [];
      if (hotspot.submissionSharePct >= 20 && hotspot.uniqueContributorCount <= 3) {
        tags.push({ label: "Concentrated Risk", color: "red darken-1" });
      }
      if (hotspot.submissionSharePct >= 15 && hotspot.uniqueContributorCount >= 8) {
        tags.push({ label: "Healthy Demand Zone", color: "green darken-1" });
      }
      if (this.isWithinLastDays(hotspot.lastSubmissionAt, 14)) {
        tags.push({ label: "Active Growth", color: "blue darken-1" });
      }
      return tags;
    },

    extractMalaysiaHour(value) {
      if (!value) {
        return null;
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return null;
      }
      const formatted = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Kuala_Lumpur",
        hour: "2-digit",
        hour12: false,
      }).format(date);
      const hour = Number(formatted);
      if (!Number.isFinite(hour)) {
        return null;
      }
      return hour;
    },

    extractMalaysiaWeekday(value) {
      if (!value) {
        return null;
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return null;
      }
      return new Intl.DateTimeFormat("en-US", {
        timeZone: "Asia/Kuala_Lumpur",
        weekday: "short",
      }).format(date);
    },

    formatHourRange(hour) {
      const startHour = String(hour).padStart(2, "0");
      const endHour = String((hour + 1) % 24).padStart(2, "0");
      return `${startHour}:00-${endHour}:00`;
    },

    refreshMapData() {
      if (!this.map || !window.google || !window.google.maps) {
        return;
      }

      const { google } = window;
      const weightedPoints = this.weightedPoints.map(point => ({
        location: new google.maps.LatLng(point.lat, point.lng),
        weight: point.count,
      }));

      if (!this.heatmap) {
        this.heatmap = new google.maps.visualization.HeatmapLayer({
          data: weightedPoints,
        });
        this.heatmap.setMap(this.map);
      } else {
        this.heatmap.setData(weightedPoints);
      }

      this.applyHeatmapStyle();
      this.toggleHeatmapVisibility();
      this.fitToPoints(this.filteredEntries);
      this.renderMarkers();
    },

    applyHeatmapStyle() {
      if (!this.heatmap) {
        return;
      }

      this.heatmap.set("radius", this.heatRadius);
      this.heatmap.set("opacity", this.heatOpacity);
      this.heatmap.set("gradient", [
        "rgba(59, 130, 246, 0)",
        "rgba(59, 130, 246, 0.35)",
        "rgba(34, 197, 94, 0.5)",
        "rgba(251, 191, 36, 0.7)",
        "rgba(239, 68, 68, 0.9)",
      ]);
    },

    toggleHeatmapVisibility() {
      if (!this.heatmap) {
        return;
      }
      this.heatmap.setMap(this.showHeatmap ? this.map : null);
    },

    fitToPoints(points) {
      if (!this.map || !points.length) {
        return;
      }

      const { google } = window;
      const bounds = new google.maps.LatLngBounds();
      points.forEach(point => {
        bounds.extend({ lat: point.lat, lng: point.lng });
      });

      if (points.length === 1) {
        this.map.setCenter(bounds.getCenter());
        this.map.setZoom(16);
        return;
      }

      this.map.fitBounds(bounds, 80);
    },

    clearMarkers() {
      this.markers.forEach(marker => marker.setMap(null));
      this.markers = [];
    },

    escapeHtml(text) {
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },

    renderMarkers() {
      if (!this.map || !window.google || !window.google.maps) {
        return;
      }

      this.clearMarkers();
      if (!this.showMarkers) {
        return;
      }

      const { google } = window;
      this.markers = this.markerEntries.map(point => {
        const marker = new google.maps.Marker({
          position: { lat: point.lat, lng: point.lng },
          map: this.map,
          title: `${point.brand} (${point.status})`,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: point.isDuplicate ? "#EF6C00" : "#0D47A1",
            fillOpacity: 0.85,
            strokeColor: "#FFFFFF",
            strokeWeight: 2,
            scale: 5,
          },
        });

        const timeText = point.createdAt
          ? new Date(point.createdAt).toLocaleString()
          : "Unknown time";
        const brandText = this.escapeHtml(point.brand);
        const statusText = this.escapeHtml(point.status);
        marker.addListener("click", () => {
          if (!this.infoWindow) {
            return;
          }
          this.infoWindow.setContent(
            `<div style="min-width:170px;">
              <strong>${brandText}</strong><br/>
              Status: ${statusText}<br/>
              Time: ${timeText}
            </div>`
          );
          this.infoWindow.open(this.map, marker);
        });
        return marker;
      });
    },

    focusHotspot(hotspot) {
      if (!this.map) {
        return;
      }
      this.map.panTo({ lat: hotspot.lat, lng: hotspot.lng });
      this.map.setZoom(17);
    },

    async fetchInventoryInsights(force = false) {
      const cached = !force ? this.getCachedInsight("inventory") : null;
      if (cached) {
        this.inventoryInsightsPayload = cached;
        this.inventoryInsightsLoadState = "ok";
        return;
      }
      if (this.inventoryInsightsLoadState === "loading") {
        return;
      }
      this.inventoryInsightsLoadState = "loading";
      try {
        const res = await hotspotApi.getInventoryInsights();
        const payload = this.parseInventoryInsightsPayload(res);
        if (payload) {
          this.inventoryInsightsPayload = payload;
          this.setCachedInsight("inventory", payload);
          this.inventoryInsightsLoadState = "ok";
        } else {
          this.inventoryInsightsPayload = null;
          this.inventoryInsightsLoadState = "error";
        }
      } catch (error) {
        this.inventoryInsightsPayload = null;
        this.inventoryInsightsLoadState = "error";
      }
    },

    async fetchBrandInsights(force = false) {
      const cached = !force ? this.getCachedInsight("brand") : null;
      if (cached) {
        this.brandInsightsPayload = cached;
        this.brandInsightsLoadState = "ok";
        return;
      }
      if (this.brandInsightsLoadState === "loading") {
        return;
      }
      this.brandInsightsLoadState = "loading";
      try {
        const res = await hotspotApi.getBrandInsights({ status: "APPROVED" });
        const payload = this.parseBrandInsightsPayload(res);
        this.brandInsightsPayload = Array.isArray(payload) ? payload : [];
        this.setCachedInsight("brand", this.brandInsightsPayload);
        this.brandInsightsLoadState = "ok";
      } catch (error) {
        this.brandInsightsPayload = [];
        this.brandInsightsLoadState = "error";
      }
    },

    async fetchTotalUserSubmissions(force = false) {
      const cached = !force ? this.getCachedInsight("users") : null;
      if (cached) {
        this.userSubmissionsPayload = cached.users;
        this.userSubmissionsTotalUniqueUsers = cached.totalUniqueUsers;
        this.userSubmissionsLoadState = "ok";
        return;
      }
      if (this.userSubmissionsLoadState === "loading") {
        return;
      }
      this.userSubmissionsLoadState = "loading";
      try {
        const res = await hotspotApi.getTotalUserSubmissions();
        const payload = this.parseUserSubmissionsPayload(res);
        const body = res && res.data;
        this.userSubmissionsTotalUniqueUsers =
          Number(body && body.totalUniqueUsers) || 0;
        this.userSubmissionsPayload = Array.isArray(payload) ? payload : [];
        this.setCachedInsight("users", {
          users: this.userSubmissionsPayload,
          totalUniqueUsers: this.userSubmissionsTotalUniqueUsers,
        });
        this.userSubmissionsLoadState = "ok";
      } catch (error) {
        this.userSubmissionsPayload = [];
        this.userSubmissionsTotalUniqueUsers = 0;
        this.userSubmissionsLoadState = "error";
      }
    },

  },

  async mounted() {
    this.fetchBrandTotals();
    this.fetchServerHotspots();
    this.$nextTick(() => {
      this.setupSectionLazyLoading();
    });
    await this.loadGoogleMapsScript();
    this.initMap();
    this.refreshMapData();
    this.logBrandCounts();
  },

  beforeDestroy() {
    if (this._lazyLoadFallbackTimer) {
      clearTimeout(this._lazyLoadFallbackTimer);
    }
    if (this.redrawTimer) {
      clearTimeout(this.redrawTimer);
    }
    this.clearMarkers();
    if (this.infoWindow) {
      this.infoWindow.close();
    }
    if (this.sectionObserver) {
      this.sectionObserver.disconnect();
      this.sectionObserver = null;
    }
  },
};
</script>

<style scoped>
.heatmap-card {
  border-radius: 14px;
}

.metric-card {
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}

.map-canvas {
  height: 520px;
  width: 100%;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
}

.legend-gradient {
  height: 10px;
  width: 100%;
  border-radius: 999px;
  background: linear-gradient(to right, #60a5fa, #22c55e, #f59e0b, #ef4444);
}

.timing-insights {
  border-radius: 14px;
  border-color: #d1d9ff !important;
  background: linear-gradient(180deg, #f9faff 0%, #fff 45%);
}

.timing-kpi {
  border: 1px solid #e3e8ff;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff;
}

.timing-kpi__label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #5c6bc0;
  font-weight: 600;
}

.timing-kpi__value {
  font-size: 20px;
  line-height: 1.2;
  font-weight: 700;
  color: #1a237e;
  margin-top: 4px;
}

.timing-bars {
  border: 1px solid #e8eaf6;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff;
}

.timing-bar-row {
  display: grid;
  grid-template-columns: 92px 1fr 130px;
  gap: 10px;
  align-items: center;
  margin-bottom: 8px;
}

.timing-bar-row:last-child {
  margin-bottom: 0;
}

.timing-bar-row__label {
  font-size: 12px;
  color: #455a64;
  font-weight: 600;
}

.timing-bar-row__track {
  height: 8px;
  border-radius: 999px;
  background: #e8eaf6;
  overflow: hidden;
}

.timing-bar-row__fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #5c6bc0, #3949ab);
}

.timing-bar-row__value {
  text-align: right;
  font-size: 12px;
  color: #546e7a;
}

.inventory-insights {
  border-radius: 14px;
  border-color: #b2dfdb !important;
  background: linear-gradient(180deg, #f3fbfa 0%, #fff 50%);
}

.inventory-kpi {
  border: 1px solid #d0ece9;
  border-radius: 10px;
  background: #fff;
  padding: 10px 12px;
}

.inventory-kpi__label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #00897b;
  font-weight: 600;
}

.inventory-kpi__value {
  font-size: 22px;
  line-height: 1.2;
  margin-top: 4px;
  color: #004d40;
  font-weight: 700;
}

.inventory-block {
  border: 1px solid #dcefeb;
  border-radius: 10px;
  background: #fff;
  padding: 10px;
}

.inventory-list {
  display: grid;
  gap: 8px;
}

.inventory-row {
  display: grid;
  grid-template-columns: 1.1fr 1fr 140px;
  gap: 8px;
  align-items: center;
}

.inventory-row__title {
  font-size: 12px;
  color: #37474f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inventory-row__bar-bg {
  height: 8px;
  border-radius: 999px;
  background: #e0f2f1;
  overflow: hidden;
}

.inventory-row__bar-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #26a69a, #00897b);
}

.inventory-row__meta {
  font-size: 11px;
  color: #546e7a;
  text-align: right;
}

.brand-insights {
  border-radius: 14px;
  border-color: #d9d7ff !important;
  background: linear-gradient(180deg, #f7f6ff 0%, #fff 50%);
}

.brand-kpi {
  border: 1px solid #e5e3ff;
  border-radius: 10px;
  background: #fff;
  padding: 10px 12px;
}

.brand-kpi__label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #5e35b1;
  font-weight: 600;
}

.brand-kpi__value {
  font-size: 22px;
  line-height: 1.2;
  margin-top: 4px;
  color: #311b92;
  font-weight: 700;
}

.brand-kpi__value--text {
  font-size: 18px;
  line-height: 1.3;
  word-break: break-word;
}

.brand-table-wrap {
  border: 1px solid #ecebff;
  border-radius: 10px;
  overflow: auto;
  background: #fff;
}

.user-submissions-insights {
  border-radius: 14px;
  border-color: #dbeafe !important;
  background: linear-gradient(180deg, #f7fbff 0%, #fff 50%);
}

.user-kpi {
  border: 1px solid #dbe8ff;
  border-radius: 10px;
  background: #fff;
  padding: 10px 12px;
}

.user-kpi__label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #1e40af;
  font-weight: 600;
}

.user-kpi__value {
  font-size: 22px;
  line-height: 1.2;
  margin-top: 4px;
  color: #1e3a8a;
  font-weight: 700;
}

.user-table-wrap {
  border: 1px solid #dbe8ff;
  border-radius: 10px;
  overflow: auto;
  background: #fff;
}

.user-id-cell {
  max-width: 220px;
}

.hotspots-snapshot__title-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #3949ab, #5c6bc0);
  vertical-align: middle;
}

.hotspot-kpi-row {
  margin: 0 -6px;
}

.hotspot-kpi {
  background: #fff;
  border: 1px solid #e8eaf6;
  border-radius: 12px;
  padding: 14px 16px;
  height: 100%;
  box-shadow: 0 1px 2px rgba(26, 35, 126, 0.06);
}

.hotspot-kpi__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #7986cb;
  margin-bottom: 6px;
}

.hotspot-kpi__value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
  color: #1a237e;
  letter-spacing: -0.02em;
}

.hotspot-kpi__unit {
  font-size: 14px;
  font-weight: 600;
  color: #5c6bc0;
  margin-left: 4px;
}

.hotspot-map-btn {
  opacity: 0.92;
}

.hotspots-snapshot {
  border-radius: 14px;
  overflow: hidden;
  border-color: #c5cae9 !important;
  background: linear-gradient(180deg, #f8faff 0%, #fff 48%);
}

.hotspots-snapshot__head {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.hotspots-snapshot__sub {
  max-width: 720px;
  line-height: 1.45;
}

.hotspot-meta-chips {
  margin: -4px;
}

.hotspot-layout {
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 14px;
}

.hotspot-layout__left,
.hotspot-layout__right {
  min-width: 0;
}

.hotspot-list-wrap {
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid #e8eaf6;
  border-radius: 12px;
  background: #fff;
}

.hotspot-list-wrap--expanded {
  max-height: 560px;
}

.hotspot-row {
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s ease;
}

.hotspot-row:last-child {
  border-bottom: none;
}

.hotspot-row:hover {
  background: #fafbff;
}

.hotspot-row--top3 {
  background: linear-gradient(90deg, rgba(99, 102, 241, 0.04), transparent);
}

.hotspot-row__rank {
  width: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eceff1;
}

.hotspot-row__rank--gold {
  background: linear-gradient(180deg, #fff8e1, #ffecb3);
}

.hotspot-row__rank--silver {
  background: linear-gradient(180deg, #fafafa, #eceff1);
}

.hotspot-row__rank--bronze {
  background: linear-gradient(180deg, #fff3e0, #ffe0b2);
}

.hotspot-row__rank-num {
  font-weight: 700;
  font-size: 15px;
  color: #37474f;
}

.hotspot-row__title {
  line-height: 1.35;
  color: #1a237e;
}

.hotspot-row__bar-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hotspot-row__bar-bg {
  flex: 1;
  height: 8px;
  border-radius: 999px;
  background: #e8eaf6;
  overflow: hidden;
}

.hotspot-row__bar-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #5c6bc0, #3949ab);
  min-width: 2px;
  transition: width 0.35s ease;
}

.hotspot-row__bar-fill--gold {
  background: linear-gradient(90deg, #f59e0b, #d97706);
}

.hotspot-row__bar-fill--silver {
  background: linear-gradient(90deg, #94a3b8, #64748b);
}

.hotspot-row__bar-fill--bronze {
  background: linear-gradient(90deg, #d97757, #b45309);
}

.hotspot-row__bar-fill--default {
  background: linear-gradient(90deg, #5c6bc0, #3949ab);
}

.hotspot-row__pct {
  min-width: 36px;
  text-align: right;
}

.hotspot-row__actions {
  min-width: 72px;
  flex-shrink: 0;
  border-left: 1px solid #f0f0f0;
  background: #fafbff;
}

.hotspot-row__count-num {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
}

.hotspot-cards {
  max-height: 480px;
  overflow-y: auto;
}

.hotspot-detail-card {
  border-radius: 10px;
  border-color: #dfe3f8 !important;
}

.hotspot-ranked-list {
  max-height: 720px;
  overflow-y: auto;
  padding-right: 2px;
}

.hotspot-ranked-row {
  border-radius: 12px !important;
  border-color: #dfe3f8 !important;
  background: #fff;
}

.hotspot-ranked-row__rank {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eceff1;
}

.hotspot-ranked-row__rank--gold {
  background: linear-gradient(180deg, #fff8e1, #ffecb3);
}

.hotspot-ranked-row__rank--silver {
  background: linear-gradient(180deg, #fafafa, #eceff1);
}

.hotspot-ranked-row__rank--bronze {
  background: linear-gradient(180deg, #fff3e0, #ffe0b2);
}

.hotspot-ranked-row__rank-num {
  font-weight: 800;
  font-size: 16px;
  color: #37474f;
}

.hotspot-ranked-row__title {
  color: #1a237e;
  line-height: 1.35;
}

.hotspot-ranked-row__label {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

@media (max-width: 1264px) {
  .hotspot-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .timing-bar-row {
    grid-template-columns: 86px 1fr;
  }

  .timing-bar-row__value {
    grid-column: 1 / -1;
    text-align: left;
    margin-left: 0;
    margin-top: -2px;
  }

  .inventory-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  .inventory-row__meta {
    text-align: left;
  }
}
</style>